import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '~/modules/hooks';
import { selectUser } from '~/selectors';
import { processPayment } from '~/services/paymentService';
import { hideLoader, showLoader } from '~/store/slices/app';
import { setPaymentCompleted, setPaymentInitiated, setPendingPlan } from '~/store/slices/user';
import { extractPlanFromUrl } from '~/utils/payment';

interface PaymentFlowProps {}

const PaymentFlow = ({}: PaymentFlowProps) => {
  const urlPlan = extractPlanFromUrl();
  const user = useAppSelector(selectUser);
  const { pathname } = useLocation();
  const plan = urlPlan || user.pendingPlan;
  const userToken = user.token;

  const hasPendingPayment = urlPlan || (user.pendingPlan && !user.paymentCompleted);
  const dispatch = useDispatch();
  const isVerificationPage = pathname === '/verify-subscription';

  const handleInitiatePayment = async () => {
    if (!plan) {
      return;
    }
    dispatch(showLoader());

    try {
      dispatch(setPaymentInitiated(true));
      dispatch(setPendingPlan(null)); // Clear pending plan once payment is initiated
      await processPayment(plan);
      dispatch(setPaymentInitiated(false));
      dispatch(setPaymentCompleted(true));
      dispatch(setPendingPlan(null));
    } catch (err) {
      console.error('Payment flow error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Payment initiation failed';
      toast.error(errorMessage);
      dispatch(setPaymentInitiated(false));
      dispatch(setPendingPlan(null));
    } finally {
      dispatch(hideLoader());
    }
  };

  // Auto-initiate payment on component mount
  useEffect(() => {
    if (hasPendingPayment && !isVerificationPage && userToken) {
      handleInitiatePayment();
    }
  }, [hasPendingPayment, isVerificationPage, userToken]);

  return null;
};

export default PaymentFlow;
